import { Col, Row, Table } from "react-bootstrap";
import type {
  FastTrackPortfolioBreakdown,
  FastTrackPortfolioHolding,
  FastTrackPortfolioResponse,
} from "../../../api/src/fasttrack/refs.service";
import type {
  MarketDataHeld,
  SecurityDetails as SecurityDetailsData,
} from "../../../api/src/security-details/security-details.types";
import { formatCurrency } from "../lib/numbers";
import {
  DID_NOT_ANSWER,
  isEmptyValue,
  Item,
  type MarketDataQuery,
  NOT_AVAILABLE,
  numberOf,
  Panel,
} from "./MarketDataPanels";

// APP-1487 (doc-security-market-data-01a150c533210e70): the Positions tab of
// a fund variant (R33 to R37, R110 to R113), from `/ref/<symbol>/portfolio`.
// Each panel shows the `date` of its own breakdown (R38), and a failed call
// shows the failed-call text in each panel (R41).

/** The text of "Firm exposure via this fund" when the firm holds none of the fund (R113). */
export const NO_EXPOSURE = "—";

/** The most holdings the top ten list shows (R37). */
const TOP_TEN = 10;

/** A FastTrack `netweight`, "6.87%" or "6.87", as "6.87%"; `NOT_AVAILABLE` for an empty value. */
const weightText = (value: unknown) => {
  const number = numberOf(value);
  return number === null ? NOT_AVAILABLE : `${number.toFixed(2)}%`;
};

/**
 * One breakdown of the portfolio (R33, R34, R35, R110): each `name` as
 * FastTrack sends it, in its order, with its `netweight`.
 */
const BreakdownPanel = ({
  title,
  breakdown,
  failed,
}: {
  title: string;
  breakdown: FastTrackPortfolioBreakdown | undefined;
  failed: boolean;
}) => {
  const items = breakdown?.datalist ?? [];
  return (
    <Panel title={title} date={failed ? null : breakdown?.date}>
      {failed ? (
        <div>{DID_NOT_ANSWER}</div>
      ) : items.length === 0 ? (
        <div>{NOT_AVAILABLE}</div>
      ) : (
        items.map((item, i) => (
          <Item
            key={`${item.name}-${i}`}
            label={isEmptyValue(item.name) ? NOT_AVAILABLE : item.name.trim()}
            value={weightText(item.netweight)}
          />
        ))
      )}
    </Panel>
  );
};

/** The `topten` holdings, at most 10, largest weight first; an empty weight last (R37). */
export function topHoldings(
  holdings: FastTrackPortfolioHolding[],
): FastTrackPortfolioHolding[] {
  const weight = (holding: FastTrackPortfolioHolding) =>
    numberOf(holding.netweight) ?? -Infinity;
  return [...holdings].sort((a, b) => weight(b) - weight(a)).slice(0, TOP_TEN);
}

/**
 * "Firm exposure via this fund" of one holding (R112, R113, D17): the market
 * value of the firm's held positions in the fund times the holding's weight,
 * in whole US dollars; "—" when the firm holds none of the fund.
 */
export function exposureText(
  held: MarketDataHeld | null | undefined,
  netweight: unknown,
): string {
  if (!held) return NO_EXPOSURE;
  const weight = numberOf(netweight);
  if (weight === null) return NOT_AVAILABLE;
  return formatCurrency(Math.round((held.marketValue * weight) / 100));
}

/** The top ten holdings list (R37, R112, R113). */
const TopTenPanel = ({
  topten,
  held,
  failed,
}: {
  topten: FastTrackPortfolioResponse["topten"] | undefined;
  held: MarketDataHeld | null | undefined;
  failed: boolean;
}) => {
  const holdings = topHoldings(topten?.datalist ?? []);
  return (
    <Panel title="Top ten holdings" date={failed ? null : topten?.date}>
      {failed ? (
        <div>{DID_NOT_ANSWER}</div>
      ) : holdings.length === 0 ? (
        <div>{NOT_AVAILABLE}</div>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Holding</th>
              <th>Ticker</th>
              <th className="text-end">Weight</th>
              <th className="text-end">Firm exposure via this fund</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((holding, i) => (
              <tr key={`${holding.ticker}-${i}`}>
                <td>
                  {isEmptyValue(holding.name)
                    ? NOT_AVAILABLE
                    : holding.name.trim()}
                </td>
                <td>
                  {isEmptyValue(holding.ticker)
                    ? NOT_AVAILABLE
                    : holding.ticker.trim()}
                </td>
                <td className="text-end">{weightText(holding.netweight)}</td>
                <td className="text-end">
                  {exposureText(held, holding.netweight)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
};

/**
 * The Positions tab of a fund variant (R47): for `fund` and `money_market`
 * the asset class, sector, region and market cap panels (R33, R110); for
 * `bond_fund` the asset class, bond sector, region, credit quality and
 * maturity panels and no sector or market cap panel (R34 to R36, R111); and
 * for each the top ten holdings (R37, R112, R113).
 */
const PositionsTab = ({
  variant,
  marketData,
}: {
  variant: SecurityDetailsData["security"]["variant"];
  marketData: MarketDataQuery;
}) => {
  if (marketData.isLoading) {
    return <div className="text-muted">Loading FastTrack data…</div>;
  }
  const data =
    marketData.data && marketData.data.variant !== "bond"
      ? marketData.data
      : undefined;
  const portfolio =
    data?.portfolio?.status === "ok" ? data.portfolio.data : undefined;
  // The route did not answer or the portfolio call failed (R41).
  const failed = marketData.isError || !portfolio;
  const isBondFund = variant === "bond_fund";
  const breakdown = (
    title: string,
    value: FastTrackPortfolioBreakdown | undefined,
  ) => (
    <Col lg={6}>
      <BreakdownPanel title={title} breakdown={value} failed={failed} />
    </Col>
  );
  return (
    <Row className="g-4">
      {breakdown("Asset class", portfolio?.assetclass)}
      {isBondFund
        ? breakdown("Bond sector", portfolio?.sectorBond)
        : breakdown("Sector", portfolio?.sector)}
      {breakdown("Region", portfolio?.region)}
      {isBondFund
        ? breakdown("Credit quality", portfolio?.bondquality)
        : breakdown("Market cap", portfolio?.marketcap)}
      {isBondFund && (
        <Col lg={6}>
          {/* FastTrack sends no maturity breakdown (R36, NG4). */}
          <Panel title="Maturity" date={null}>
            <div>{NOT_AVAILABLE}</div>
          </Panel>
        </Col>
      )}
      <Col xs={12}>
        <TopTenPanel
          topten={portfolio?.topten}
          held={data?.held}
          failed={failed}
        />
      </Col>
    </Row>
  );
};

export default PositionsTab;
